// components/GestionPicnic.js
import React, { useState, useEffect } from 'react';
import {
  Modal,
  Table,
  Button,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Space,
  message,
  Popconfirm,
  Tag,
  Row,
  Col,
  Card,
  Divider
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  CoffeeOutlined,
  ShoppingOutlined,
  ForkOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import { BASE_URL } from '../utils/constants';

const { Option } = Select;

const GestionPicnics = ({ visible, onClose, onSuccess }) => {
  const [picnics, setPicnics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalFormVisible, setModalFormVisible] = useState(false);
  const [editingPicnic, setEditingPicnic] = useState(null);
  const [form] = Form.useForm();
  
  // Filtros
  const [fechaDesde, setFechaDesde] = useState(null);
  const [fechaHasta, setFechaHasta] = useState(null);
  const [numHab, setNumHab] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  // Estados para la búsqueda de reservas
  const [reservas, setReservas] = useState([]);
  const [reservasLoading, setReservasLoading] = useState(false);
  const [selectedReserva, setSelectedReserva] = useState(null);
  const [buscarNumHab, setBuscarNumHab] = useState(null);
  const [buscarFecha, setBuscarFecha] = useState(null);

  const tipoOptions = [
    { value: 'D', label: 'Desayuno', icon: <CoffeeOutlined />, color: 'orange' },
    { value: 'A', label: 'Almuerzo', icon: <ShoppingOutlined />, color: 'green' },
    { value: 'C', label: 'Cena', icon: <ForkOutlined />, color: 'blue' }
  ];

  // Mapeo de colores para tipos de reserva
  const getTipoReservaColor = (tipo) => {
    switch(tipo) {
      case 'Entrada': return 'green';
      case 'Salida': return 'orange';
      case 'Estancia': return 'blue';
      default: return 'default';
    }
  };

  const columns = [
    {
      title: 'Fecha',
      dataIndex: 'FechaPicnic',
      key: 'FechaPicnic',
      width: 120,
      render: (fecha) => fecha ? dayjs(fecha).format('DD/MM/YYYY') : '-',
      sorter: (a, b) => new Date(a.FechaPicnic) - new Date(b.FechaPicnic)
    },
    {
      title: 'Habitación',
      dataIndex: 'NumHab',
      key: 'NumHab',
      width: 100,
      render: (num) => num === 0 ? <Tag>General</Tag> : <Tag color="blue">{num}</Tag>
    },
    {
      title: 'Tipo Picnic',
      dataIndex: 'tipo',
      key: 'tipo',
      width: 100,
      render: (tipo) => {
        const tipoInfo = tipoOptions.find(t => t.value === tipo);
        return (
          <Tag color={tipoInfo?.color} icon={tipoInfo?.icon}>
            {tipoInfo?.label}
          </Tag>
        );
      }
    },
    {
      title: 'Tipo Reserva',
      dataIndex: 'TipoReserva',
      key: 'TipoReserva',
      width: 120,
      render: (tipo) => {
        return (
          <Tag color={getTipoReservaColor(tipo)}>
            {tipo || 'N/A'}
          </Tag>
        );
      }
    },    
    {
      title: 'Cantidad',
      dataIndex: 'NumPicnic',
      key: 'NumPicnic',
      width: 100,
      render: (num) => <Tag color="purple" style={{ fontWeight: 'bold' }}>{num}</Tag>
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="¿Eliminar picnic?"
            description="Esta acción no se puede deshacer"
            onConfirm={() => handleDelete(record.numero)}
            okText="Sí"
            cancelText="No"
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              size="small"
            />
          </Popconfirm>
        </Space>
      )
    }
  ];

  const fetchPicnics = async (params = {}) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const queryParams = {
        page: params.page || pagination.current,
        pageSize: params.pageSize || pagination.pageSize,
        ...(fechaDesde && { fechaDesde: fechaDesde.format('YYYY-MM-DD') }),
        ...(fechaHasta && { fechaHasta: fechaHasta.format('YYYY-MM-DD') }),
        ...(numHab && numHab !== '' && { numHab })
      };
      
      const response = await axios.get(`${BASE_URL}/api/serviciococina/picnics`, {
        headers: { Authorization: `Bearer ${token}` },
        params: queryParams
      });
      
      setPicnics(response.data.data || []);
      if (response.data.pagination) {
        setPagination({
          ...pagination,
          total: response.data.pagination.totalRecords,
          current: response.data.pagination.currentPage,
          pageSize: response.data.pagination.pageSize
        });
      }
    } catch (error) {
      console.error('Error cargando picnics:', error);
      message.error('Error al cargar los picnics');
    } finally {
      setLoading(false);
    }
  };

  // Función para buscar reservas por habitación y fecha
  const buscarReservas = async (numHab, fecha) => {
    if (!numHab || !fecha) {
      setReservas([]);
      setSelectedReserva(null);
      return;
    }

    setReservasLoading(true);
    try {
      const token = localStorage.getItem('token');
      const fechaFormateada = fecha.format('YYYY-MM-DD');
      
      // Enviar 'all' como tipo para obtener todas las reservas
      const response = await axios.get(
        `${BASE_URL}/api/serviciococina/picnics/${numHab}/${fechaFormateada}/all`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const reservasData = response.data.data || [];
      setReservas(reservasData);
      setSelectedReserva(null);
      
      // Limpiar campos de reserva en el formulario
      form.setFieldsValue({
        numreserva: null,
        linea: null,
        entrada: null,
        salida: null,
        NumPicnic: 1
      });
      
      // Si hay reservas, mostrar mensaje
      if (reservasData.length > 0) {
        const tipos = [...new Set(reservasData.map(r => r.TipoReserva))];
        // message.success(`Se encontraron ${reservasData.length} reservas: ${tipos.join(', ')}`);
      } else {
        message.info('No se encontraron reservas para esta habitación y fecha');
      }
      
    } catch (error) {
      console.error('Error buscando reservas:', error);
      message.error('Error al buscar reservas');
      setReservas([]);
    } finally {
      setReservasLoading(false);
    }
  };

  const cargarReservaPorTipo = async (tipoReserva) => {
    if (!buscarNumHab || !buscarFecha || !tipoReserva) return;

    try {
      const token = localStorage.getItem('token');
      const fechaFormateada = buscarFecha.format('YYYY-MM-DD');
      
      const response = await axios.get(
        `${BASE_URL}/api/serviciococina/picnics/${buscarNumHab}/${fechaFormateada}/${tipoReserva}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const reservasData = response.data.data || [];
      if (reservasData.length > 0) {
        const reserva = reservasData[0];
        setSelectedReserva(reserva);
        
        // Actualizar campos del formulario
        form.setFieldsValue({
          numreserva: reserva.numreserva,
          linea: reserva.linea,
          entrada: dayjs(reserva.entrada),
          salida: dayjs(reserva.salida),
          // La cantidad por defecto será el número de pax
          NumPicnic: reserva.pax || 1
        });
        
        message.success(`Reserva de ${tipoReserva} seleccionada (Pax: ${reserva.pax})`);
      } else {
        message.warning(`No se encontró reserva de tipo ${tipoReserva}`);
        setSelectedReserva(null);
      }
    } catch (error) {
      console.error('Error cargando reserva por tipo:', error);
      message.error('Error al cargar la reserva');
    }
  };

  useEffect(() => {
    if (visible) {
      fetchPicnics();
    }
  }, [visible, fechaDesde, fechaHasta, numHab]);

  const handleCreate = () => {
    setEditingPicnic(null);
    setReservas([]);
    setSelectedReserva(null);
    setBuscarNumHab(null);
    setBuscarFecha(null);
    form.resetFields();
    
    // Calcular fecha de mañana
    const manana = dayjs().add(1, 'day');
    
    form.setFieldsValue({
      NumHab: 0,
      FechaPicnic: manana,  // Fecha por defecto: mañana
      tipo: 'D',
      NumPicnic: 1,
      numreserva: null,
      linea: null,
      entrada: null,
      salida: null
    });
    
    setModalFormVisible(true);
  };

const handleEdit = (record) => {
  setEditingPicnic(record);
  setBuscarNumHab(record.NumHab);
  setBuscarFecha(dayjs(record.FechaPicnic));
  
  // Cargar los datos del formulario
  form.setFieldsValue({
    NumHab: record.NumHab,
    FechaPicnic: dayjs(record.FechaPicnic),
    tipo: record.tipo,
    NumPicnic: record.NumPicnic,
    numreserva: record.numreserva || null,
    linea: record.linea || null,
    entrada: record.entrada ? dayjs(record.entrada) : null,
    salida: record.salida ? dayjs(record.salida) : null
  });
  
  // Buscar todas las reservas disponibles para esa habitación y fecha
  buscarReservasConSeleccion(record.NumHab, dayjs(record.FechaPicnic), record.numreserva, record.linea);
  
  setModalFormVisible(true);
};

const buscarReservasConSeleccion = async (numHab, fecha, numreservaActual, lineaActual) => {
  if (!numHab || !fecha) {
    setReservas([]);
    setSelectedReserva(null);
    return;
  }

  setReservasLoading(true);
  try {
    const token = localStorage.getItem('token');
    const fechaFormateada = fecha.format('YYYY-MM-DD');
    
    // Buscar todas las reservas
    const response = await axios.get(
      `${BASE_URL}/api/serviciococina/picnics/${numHab}/${fechaFormateada}/all`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    
    const reservasData = response.data.data || [];
    setReservas(reservasData);
    
    // Buscar la reserva actual en la lista
    let reservaSeleccionada = null;
    if (numreservaActual && lineaActual) {
      reservaSeleccionada = reservasData.find(
        r => r.numreserva === numreservaActual && r.linea === lineaActual
      );
    }
    
    // Si no se encuentra la reserva actual, seleccionar la primera
    if (!reservaSeleccionada && reservasData.length > 0) {
      reservaSeleccionada = reservasData[0];
    }
    
    setSelectedReserva(reservaSeleccionada);
    
    // Si hay una reserva seleccionada, actualizar los campos del formulario
    if (reservaSeleccionada) {
      form.setFieldsValue({
        numreserva: reservaSeleccionada.numreserva,
        linea: reservaSeleccionada.linea,
        entrada: dayjs(reservaSeleccionada.entrada),
        salida: dayjs(reservaSeleccionada.salida),
        // La cantidad por defecto será el número de pax
        NumPicnic: reservaSeleccionada.pax || 1
      });
      
      // // Mostrar mensaje con la reserva seleccionada
      // if (numreservaActual && lineaActual) {
      //   message.info(`Reserva actual: ${reservaSeleccionada.TipoReserva} (Pax: ${reservaSeleccionada.pax})`);
      // } else {
      //   message.info(`Reserva seleccionada: ${reservaSeleccionada.TipoReserva} (Pax: ${reservaSeleccionada.pax})`);
      // }
    }
    
  } catch (error) {
    console.error('Error buscando reservas:', error);
    message.error('Error al buscar reservas');
    setReservas([]);
  } finally {
    setReservasLoading(false);
  }
};


  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const token = localStorage.getItem('token');
      
      const data = {
        NumHab: values.NumHab || 0,
        FechaPicnic: values.FechaPicnic.format('YYYY-MM-DD'),
        tipo: values.tipo,
        NumPicnic: values.NumPicnic,
        numreserva: values.numreserva || null,
        linea: values.linea || null
      };
      
      // console.log(" data => ",data);
      
      if (editingPicnic) {
        await axios.put(
          `${BASE_URL}/api/serviciococina/picnics/${editingPicnic.numero}`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        message.success('Picnic actualizado correctamente');
      } else {
        await axios.post(
          `${BASE_URL}/api/serviciococina/picnics`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        message.success('Picnic creado correctamente');
      }
      
      setModalFormVisible(false);
      fetchPicnics();
      if (onSuccess) onSuccess();
      
    } catch (error) {
      console.error('Error guardando picnic:', error);
      message.error(error.response?.data?.error || 'Error al guardar');
    }
  };

  const handleDelete = async (numero) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${BASE_URL}/api/serviciococina/picnics/${numero}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success('Picnic eliminado correctamente');
      fetchPicnics();
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Error eliminando picnic:', error);
      message.error('Error al eliminar');
    }
  };

  const handleResetFilters = () => {
    setFechaDesde(null);
    setFechaHasta(null);
    setNumHab('');
    setPagination({ ...pagination, current: 1 });
  };

  const handleNumHabChange = (value) => {
    const fecha = form.getFieldValue('FechaPicnic');
    setBuscarNumHab(value);
    if (value && fecha) {
      // En creación o edición, buscar todas las reservas
      buscarReservasConSeleccion(value, fecha, null, null);
    } else {
      setReservas([]);
      setSelectedReserva(null);
    }
  };

  const handleFechaChange = (date) => {
    const numHab = form.getFieldValue('NumHab');
    setBuscarFecha(date);
    if (numHab && date) {
      // En creación o edición, buscar todas las reservas
      buscarReservasConSeleccion(numHab, date, null, null);
    } else {
      setReservas([]);
      setSelectedReserva(null);
    }
  };

  const handleTipoReservaChange = (value) => {
    if (buscarNumHab && buscarFecha && value) {
      // Buscar la reserva específica por tipo
      cargarReservaPorTipo(value);
    }
  };

  return (
    <>
      <Modal
        title="Gestión de Picnics"
        open={visible}
        onCancel={onClose}
        footer={null}
        width={1000}
        destroyOnClose
      >
        <Card size="small" style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]} align="middle">
            <Col span={8}>
              <DatePicker
                placeholder="Fecha desde"
                style={{ width: '100%' }}
                format="DD/MM/YYYY"
                value={fechaDesde}
                onChange={setFechaDesde}
              />
            </Col>
            <Col span={8}>
              <DatePicker
                placeholder="Fecha hasta"
                style={{ width: '100%' }}
                format="DD/MM/YYYY"
                value={fechaHasta}
                onChange={setFechaHasta}
              />
            </Col>
            <Col span={6}>
              <Input
                placeholder="Nº Habitación"
                value={numHab}
                onChange={(e) => setNumHab(e.target.value)}
                prefix={<SearchOutlined />}
              />
            </Col>
            <Col span={2}>
              <Button
                icon={<ReloadOutlined />}
                onClick={handleResetFilters}
              />
            </Col>
          </Row>
        </Card>
        
        <div style={{ marginBottom: 16, textAlign: 'right' }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
          >
            Nuevo Picnic
          </Button>
        </div>
        
        <Table
          columns={columns}
          dataSource={picnics}
          rowKey="numero"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} picnics`,
            onChange: (page, pageSize) => {
              setPagination({ ...pagination, current: page, pageSize });
              fetchPicnics({ page, pageSize });
            }
          }}
          scroll={{ x: 700 }}
        />
      </Modal>

      <Modal
        title={editingPicnic ? 'Editar Picnic' : 'Nuevo Picnic'}
        open={modalFormVisible}
        onOk={handleSave}
        onCancel={() => setModalFormVisible(false)}
        okText={editingPicnic ? 'Actualizar' : 'Crear'}
        cancelText="Cancelar"
        destroyOnClose
        getContainer={document.body}
        style={{ top: 20 }}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ NumHab: 0, tipo: 'D', NumPicnic: 1,FechaPicnic: dayjs().add(1, 'day'), }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="NumHab"
                label="Nº Habitación"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  placeholder="0 = General"
                  min={0}
                  onChange={handleNumHabChange}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="FechaPicnic"
                label="Fecha"
                rules={[{ required: true, message: 'Seleccione la fecha' }]}
              >
                <DatePicker 
                  style={{ width: '100%' }} 
                  format="DD/MM/YYYY"
                  onChange={handleFechaChange}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Selector de Tipo de Reserva */}
          {reservas.length > 0 && (
            <Form.Item
              label="Tipo de Reserva"
              tooltip="Seleccione el tipo de reserva para cargar los datos"
            >
              <Select
                placeholder="Seleccione un tipo de reserva"
                style={{ width: '100%' }}
                onChange={handleTipoReservaChange}
                loading={reservasLoading}
                disabled={reservasLoading}
                value={selectedReserva?.TipoReserva} // Esto muestra el valor seleccionado
              >
                {reservas.map((reserva, index) => (
                  <Option key={index} value={reserva.TipoReserva}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Space>
                        <Tag color={getTipoReservaColor(reserva.TipoReserva)}>
                          {reserva.TipoReserva}
                        </Tag>
                        <span>Pax: {reserva.pax}</span>
                      </Space>
                      <Space size="small">
                        <span style={{ fontSize: '12px', color: '#666' }}>
                          E: {dayjs(reserva.entrada).format('DD/MM/YYYY')}
                        </span>
                        <span style={{ fontSize: '12px', color: '#666' }}>
                          S: {dayjs(reserva.salida).format('DD/MM/YYYY')}
                        </span>
                      </Space>
                    </div>
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {/* Campos de reserva (no editables) */}
          {selectedReserva && (
            <>
              <Divider>Datos de la Reserva</Divider>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="numreserva"
                    label="Nº Reserva"
                  >
                    <Input disabled />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="linea"
                    label="Línea"
                  >
                    <Input disabled />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="entrada"
                    label="Fecha Entrada"
                  >
                    <DatePicker disabled style={{ width: '100%' }} format="DD/MM/YYYY" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="salida"
                    label="Fecha Salida"
                  >
                    <DatePicker disabled style={{ width: '100%' }} format="DD/MM/YYYY" />
                  </Form.Item>
                </Col>
              </Row>
            </>
          )}

          <Form.Item
            name="tipo"
            label="Tipo de Servicio"
            rules={[{ required: true, message: 'Seleccione el tipo' }]}
          >
            <Select>
              {tipoOptions.map(opt => (
                <Option key={opt.value} value={opt.value}>
                  <Space>
                    {opt.icon}
                    {opt.label}
                  </Space>
                </Option>
              ))}
            </Select>
          </Form.Item>
          
          <Form.Item
            name="NumPicnic"
            label="Número de Picnics"
            rules={[
              { required: true, message: 'Ingrese la cantidad' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const maxPax = selectedReserva?.pax || Infinity;
                  if (value && value > maxPax) {
                    return Promise.reject(new Error(`La cantidad no puede superar los ${maxPax} pax de la reserva`));
                  }
                  if (value && value < 1) {
                    return Promise.reject(new Error('La cantidad mínima es 1'));
                  }
                  return Promise.resolve();
                },
              }),
            ]}
            extra={selectedReserva ? `Máximo permitido: ${selectedReserva.pax} pax` : ''}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={1}
              placeholder="Cantidad de picnics"
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default GestionPicnics;